import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import toTitleCase from '@hooks/toTitleCase';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import React from 'react';
import { StyleSheet, Platform, Pressable, Image } from 'react-native';
import { View, Text } from '@constants/Themed';
import * as Animatable from 'react-native-animatable';

// Define a type for the integration object
type ConnectedPlatformsProps = {
  integration: string; 
  logo: string;
  hideButton?: boolean;
};

const ConnectedPlatforms: React.FC<ConnectedPlatformsProps> = ({ integration, logo, hideButton }) => {
  const theme = themeSelector();

  const getPlatformInfo = (platformName: string) => {
    const platforms: { [key: string]: { description: string; link: string; color: string } } = {
      linkedin: {
        description: "Professional networking and B2B marketing platform",
        link: "https://linkedin.com",
        color: theme ? "#0077b5" : "#38bdf8"
      },
      tiktok: {
        description: "Short-form video content and viral marketing",
        link: "https://tiktok.com",
        color: theme ? "#ff0050" : "#ec4899"
      },
      instagram: {
        description: "Visual storytelling and influencer marketing",
        link: "https://instagram.com",
        color: theme ? "#e4405f" : "#f59e0b"
      },
      x: {
        description: "Real-time updates and community engagement",
        link: "https://x.com",
        color: theme ? "#1da1f2" : "#0ea5e9"
      },
      facebook: {
        description: "Social media advertising and community building",
        link: "https://facebook.com",
        color: theme ? "#1877f2" : "#3b82f6"
      },
      whatsapp: {
        description: "Direct messaging and customer communication",
        link: "https://whatsapp.com",
        color: theme ? "#25d366" : "#10b981"
      }
    };
    return platforms[platformName.toLowerCase()] || {
      description: "Social media platform integration",
      link: "#",
      color: theme ? "#6366f1" : "#8b5cf6"
    };
  };

  const platformInfo = getPlatformInfo(integration);

  return (
    <Animatable.View
      animation="zoomIn"
      duration={600}
      style={[
        modernStyles.container,
        {
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)",
          shadowColor: theme ? "#000" : "#fff",
          shadowOpacity: theme ? 0.08 : 0.15,
        }
      ]}
    >
      {/* Platform Logo Section */}
      <View style={[
        modernStyles.logoSection,
        { 
          backgroundColor: theme ? "#f8fafc" : "#334155"
        }
      ]}>
        {logo ? (
          <Image
            source={{ uri: logo }}
            style={modernStyles.logoImage}
            resizeMode="contain"
          />
        ) : (
          <View style={[
            modernStyles.fallbackIcon,
            { backgroundColor: platformInfo.color + '20' }
          ]}>
            <MaterialIcons
              name="link"
              size={32}
              color={platformInfo.color}
            />
          </View>
        )}
      </View>

      {/* Content Section */}
      <View style={modernStyles.contentSection}>
        <View style={modernStyles.headerRow}>
          <Text style={[
            modernStyles.platformName,
            { color: theme ? "#1e293b" : "#f1f5f9" }
          ]}>
            {toTitleCase(integration)}
          </Text>
          <View style={[
            modernStyles.statusBadge,
            { backgroundColor: platformInfo.color + '15' }
          ]}>
            <View style={[
              modernStyles.statusDot,
              { backgroundColor: platformInfo.color }
            ]} />
            <Text style={[
              modernStyles.statusText,
              { color: platformInfo.color }
            ]}>
              Connected
            </Text>
          </View>
        </View>
        
        <Text style={[
          modernStyles.description,
          { color: theme ? "#64748b" : "#94a3b8" }
        ]}>
          {platformInfo.description}
        </Text>

        {!hideButton && (
        <Pressable 
          style={({ pressed }) => [
            modernStyles.actionButton,
            {
              backgroundColor: pressed 
                ? (theme ? platformInfo.color + '10' : platformInfo.color + '20')
                : 'transparent',
              borderColor: platformInfo.color + '40'
            }
          ]}
          onPress={() => {
            // Handle platform link navigation
            if (Platform.OS === 'web') {
              window.open(platformInfo.link, '_blank');
            }
          }}
        >
          <Text style={[
            modernStyles.actionText,
            { color: platformInfo.color }
          ]}>
            View Platform
          </Text>
          <AntDesign 
            name="arrow-right" 
            size={14} 
              color={platformInfo.color} 
            />
          </Pressable>
          )}
      </View>
    </Animatable.View>
  );
};

const modernStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    borderRadius: 20,
    minHeight: 140,
    shadowOffset: { width: 0, height: 8 },
    shadowRadius: 16,
    elevation: 8,
    borderWidth: 1,
    overflow: 'hidden',
    marginVertical: 6,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(10px)',
        maxWidth: 400,
      }
    })
  },

  logoSection: {
    width: 120,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },

  logoImage: {
    maxWidth: 80,
    maxHeight: 40,
    width: '100%',
    height: '100%',
    borderRadius: 8,
  },

  fallbackIcon: {
    width: 60,
    height: 60,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },

  contentSection: {
    flex: 1,
    padding: 20,
    gap: 12,
    justifyContent: 'space-between',
  },

  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },

  platformName: {
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 24,
    flex: 1,
  },

  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },

  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },

  statusText: {
    fontSize: 11,
    fontWeight: '600',
  },

  description: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '500',
    flex: 1,
  },

  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    alignSelf: 'flex-start',
  },

  actionText: {
    fontSize: 13,
    fontWeight: '600',
  },
});

export default ConnectedPlatforms;